import React, { useContext, useEffect, useState } from 'react';
import { PeFilterContext } from '../../Contexts/PeFilterContext';
import MultiSelectEntity from '../RefineSearch/MultiSelectEntity';
import Popup from '../Common/Popup';
import Constants from '../../Constants';
import { FaInfoCircle } from 'react-icons/fa';
import { Tooltip } from 'antd';
import DefinitionModal from '../DefinitionModal';

/**
 * The `StageFilter` function is a React component that renders a filter for selecting stages, with
 * options that can be expanded or collapsed.
 * @returns a JSX element.
 */
function StageFilter({ setStageOptions, StageOptions, customPathname, menu, label = "Stage", isPopupOpen, setIsPopupOpen }) {

    const { Stages, refineSearchFilters, accessControl } = useContext(PeFilterContext);
    
    // Use internal state only if props are not provided (for backward compatibility)
    const [internalPopupOpen, setInternalPopupOpen] = useState(false);
    const popupOpen = isPopupOpen !== undefined ? isPopupOpen : internalPopupOpen;
    const setPopupOpen = setIsPopupOpen !== undefined ? setIsPopupOpen : setInternalPopupOpen;

    useEffect(() => {
        const stageIds = refineSearchFilters[customPathname]?.stage?.map(value => value.id) ?? [];
        
        // Choose which stages to display based on `customPathname`
        const stagesToDisplay = menu === "vc" ? Stages.filter(item => item.name === "Early" || item.name === "Growth") : Stages;
        
        setStageOptions(
            stagesToDisplay.map(stage => ({
                ...stage,
                isChecked: stageIds.includes(stage.id)
            }))
        );
        
    }, [Stages, customPathname, refineSearchFilters, setStageOptions]);


    const handleStageChange = (e) => {
        const { id, checked, name, filteredIds } = e.target;
        
        // Handle "Select All" / "Deselect All" functionality
        if (name === 'filteredSelect') {
            const updatedStages = StageOptions.map(stage =>
                filteredIds.includes(stage.id) ? { ...stage, isChecked: checked } : stage
            );
            setStageOptions(updatedStages);
        } else {
            // Handle individual checkbox changes
            const updatedStages = StageOptions.map(stage =>
                stage.id === parseInt(id) ? { ...stage, isChecked: checked } : stage
            );
            setStageOptions(updatedStages);
        }
    };

    const selectedStageLength = StageOptions?.filter(option => option.isChecked)?.length ?? 0;

    const definitionInfo = menu === "vc"
        ? {
            Early_Stage: Constants.stage.Early_Stage,
            Growth_Stage: Constants.stage.Growth_Stage
        }
        : Constants.stage;

    const [showDefModal, setShowDefModal] = useState(false);
    
    const openModal = (e) => {
        e.stopPropagation(); // Prevent button click from toggling popup
        setShowDefModal(true);
    }
    
    const closeModal = () => {
        setShowDefModal(false);
    }

    return (
        <div className="relative w-full md:inline-block">
            <button
                type="button"
                className="h-10 rounded-md px-4 py-1 text-gray-500 hover:bg-gray-100 text-sm shadow-md border border-gray-300 text-left flex items-center justify-between w-full md:w-[90%]"
                onClick={() => setPopupOpen(!popupOpen)}
            >
                <span className="truncate pr-2 flex items-center gap-2">
                    {label}
                    <Tooltip title='Definitions'>
                        <FaInfoCircle 
                            size={12} 
                            color="grey" 
                            className="cursor-pointer" 
                            onClick={openModal}
                        />
                    </Tooltip>
                    <span className="opacity-60 ml-2 text-xs text-ellipsis">e.g. {StageOptions.slice(0,2).map(item => item.name).join(', ')}</span>
                </span>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {selectedStageLength > 0 && (
                        <span className="bg-white rounded-full px-2 py-0.5 text-xs font-medium border border-gray-400">
                            {selectedStageLength}
                        </span>
                    )}
                    <span className={`transition-transform duration-200 text-xl font-semibold ${popupOpen ? 'rotate-[270deg]' : 'rotate-90'}`}>
                        ›
                    </span>
                </div>
            </button>

            {popupOpen && (
                <Popup 
                    onClose={() => setPopupOpen(false)}
                    label={label}
                    definitionInfo={definitionInfo}
                >
                    <div className="p-4">
                        <MultiSelectEntity
                            handleEntityChange={handleStageChange}
                            primaryEntity={StageOptions}
                            disabled={accessControl?.pe_stage?.id?.length > 0
                                ? StageOptions.filter(op => !accessControl.pe_stage.id.includes(op.id))
                                : []}
                            searchBox={false}
                        />
                    </div>
                </Popup>
            )}
            {showDefModal && <DefinitionModal label={label} definitionInfo={definitionInfo} onClose={closeModal}/>}
        </div>
    )
}

export default StageFilter